import React, { useState, useEffect } from 'react';
import {
  Modal,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  Switch,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Divider,
  TextField,
  FormControlLabel,
  IconButton,
  Tooltip,
  Stack,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useNotify } from 'react-admin';
import config from '../config';
import { getValidToken } from '../services/tokenRefreshService';

interface Holiday {
  id: string;
  name: string;
  date: string;
  isEnabled: boolean;
  isSystemDefault: boolean;
}

/** A day treated as a public holiday that is not in the SA calendar. */
interface HolidayOverride {
  id: string;
  date: string;
  reason: string | null;
  openTime: string | null;
  closeTime: string | null;
}

interface PublicHolidayManagerProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Public Holiday Manager
 *
 * Two things, in the order you think about them:
 *
 * 1. The SA holiday calendar. Enabling one means every active store trades that
 *    day on its OWN rule — closed, normal, or a set window — applied at 5am and
 *    put back at midnight. The rule itself is per store and lives in Store
 *    Command Center, because a branch that shuts on Christmas shuts on Heritage
 *    Day too.
 *
 * 2. Overrides, for days the calendar does not know about — a declared day of
 *    mourning, a strike. An override applies to EVERY store at once and outranks
 *    each store's rule; one for today takes effect immediately rather than
 *    waiting for the 5am run.
 *
 * This used to say "Enabled holidays auto-apply 6pm liquor cutoff at 5am", which
 * described the whole of what the old system did and is now meaningless — we do
 * not sell liquor.
 */
export function PublicHolidayManager({ open, onClose }: PublicHolidayManagerProps) {
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [overrides, setOverrides] = useState<HolidayOverride[]>([]);
  const [loading, setLoading] = useState(false);
  const notify = useNotify();

  // Add-override form
  const [newDate, setNewDate] = useState('');
  const [newReason, setNewReason] = useState('');
  const [closedAllDay, setClosedAllDay] = useState(true);
  const [newOpen, setNewOpen] = useState('09:00');
  const [newClose, setNewClose] = useState('17:00');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      fetchHolidays();
      fetchOverrides();
    }
  }, [open]);

  const authHeaders = async () => ({
    Authorization: `Bearer ${await getValidToken()}`,
    'Content-Type': 'application/json',
  });

  const fetchHolidays = async () => {
    setLoading(true);
    const token = await getValidToken();

    try {
      const response = await fetch(`${config.API_URL}/admin/public-holidays/upcoming`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (!response.ok) {
        throw new Error('Failed to fetch holidays');
      }

      const data = await response.json();
      setHolidays(data);
    } catch (error) {
      notify('Failed to load holidays', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const fetchOverrides = async () => {
    const token = await getValidToken();
    try {
      const response = await fetch(`${config.API_URL}/admin/public-holidays/overrides`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('Failed to fetch overrides');
      const json = await response.json();
      setOverrides(json.data || []);
    } catch (error) {
      notify('Failed to load overrides', { type: 'error' });
    }
  };

  const handleToggle = async (holidayId: string, currentValue: boolean) => {
    const token = await getValidToken();

    // Optimistically update UI
    setHolidays(holidays.map(h =>
      h.id === holidayId ? { ...h, isEnabled: !currentValue } : h
    ));

    try {
      const response = await fetch(`${config.API_URL}/admin/public-holidays/${holidayId}/toggle`, {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ isEnabled: !currentValue }),
      });

      if (!response.ok) {
        throw new Error('Failed to toggle holiday');
      }

      const json = await response.json();
      notify(json.message || 'Holiday updated', { type: 'success' });
    } catch (error) {
      // Revert on error
      setHolidays(holidays.map(h =>
        h.id === holidayId ? { ...h, isEnabled: currentValue } : h
      ));
      notify('Failed to update holiday', { type: 'error' });
    }
  };

  const handleAddOverride = async () => {
    if (!newDate) {
      notify('Pick a date', { type: 'warning' });
      return;
    }
    if (!closedAllDay && (!newOpen || !newClose)) {
      notify('Set both an opening and a closing time, or choose closed all day', { type: 'warning' });
      return;
    }

    setSaving(true);
    try {
      const response = await fetch(`${config.API_URL}/admin/public-holidays/overrides`, {
        method: 'POST',
        headers: await authHeaders(),
        body: JSON.stringify({
          date: newDate,
          reason: newReason || undefined,
          // Omitting the window is what means "closed", so send nothing rather
          // than empty strings.
          openTime: closedAllDay ? null : `${newOpen}:00`,
          closeTime: closedAllDay ? null : `${newClose}:00`,
        }),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || 'Failed to add override');

      notify(json.message || 'Override added', { type: 'success' });
      setNewDate('');
      setNewReason('');
      setClosedAllDay(true);
      await fetchOverrides();
    } catch (error: any) {
      notify(error.message || 'Failed to add override', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOverride = async (id: string) => {
    try {
      const response = await fetch(`${config.API_URL}/admin/public-holidays/overrides/${id}`, {
        method: 'DELETE',
        headers: await authHeaders(),
      });
      const json = await response.json();
      if (!response.ok) throw new Error(json.message || 'Failed to remove override');

      notify(json.message || 'Override removed', { type: 'success' });
      await fetchOverrides();
    } catch (error: any) {
      notify(error.message || 'Failed to remove override', { type: 'error' });
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-ZA', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const describeOverride = (o: HolidayOverride) =>
    o.openTime && o.closeTime
      ? `All stores open ${o.openTime.slice(0, 5)}–${o.closeTime.slice(0, 5)}`
      : 'All stores closed';

  const today = new Date().toISOString().slice(0, 10);

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 640,
          maxHeight: '85vh',
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 24,
          p: 4,
          overflow: 'auto',
        }}
      >
        <Typography variant="h5" gutterBottom>
          South African Public Holidays
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Enabled holidays apply public holiday times to stores. Each store's holiday
          rule — closed, normal hours, or a set window — is configured per store in
          Store Command Center.
        </Typography>

        {/* The default rule is 'closed', so an enabled holiday shuts every store
            nobody has configured. Better said here than discovered on the day. */}
        <Alert severity="warning" sx={{ mt: 2 }}>
          A store with no holiday rule set is treated as <strong>closed</strong>. Set each
          store's rule before the next enabled holiday.
        </Alert>

        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            {holidays.length === 0 ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                No upcoming holidays found
              </Alert>
            ) : (
              <List sx={{ mt: 2 }}>
                {holidays.map((holiday) => (
                  <ListItem
                    key={holiday.id}
                    sx={{
                      border: '1px solid #e0e0e0',
                      borderRadius: 1,
                      mb: 1,
                      '&:hover': {
                        backgroundColor: '#f5f5f5',
                      },
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">{holiday.name}</Typography>
                          <Chip
                            label="Holiday hours from 5am"
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={formatDate(holiday.date)}
                    />
                    <Switch
                      checked={holiday.isEnabled}
                      onChange={() => handleToggle(holiday.id, holiday.isEnabled)}
                      color="primary"
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </>
        )}

        <Divider sx={{ my: 3 }} />

        <Typography variant="h6" gutterBottom>
          Public Holiday Override
        </Typography>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          Treat a date as a public holiday whether or not it appears above — a declared
          day of mourning, a strike, an unplanned closure. An override applies to{' '}
          <strong>every store at once</strong> and outranks each store's own rule. One
          dated today takes effect immediately.
        </Typography>

        {overrides.length > 0 && (
          <List sx={{ mt: 1 }}>
            {overrides.map((o) => (
              <ListItem
                key={o.id}
                sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}
                secondaryAction={
                  <Tooltip title="Remove override">
                    <IconButton edge="end" onClick={() => handleDeleteOverride(o.id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                }
              >
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="subtitle1">{formatDate(o.date)}</Typography>
                      {o.date === today && <Chip label="Today" size="small" color="warning" />}
                    </Box>
                  }
                  secondary={`${describeOverride(o)}${o.reason ? ` — ${o.reason}` : ''}`}
                />
              </ListItem>
            ))}
          </List>
        )}

        <Stack spacing={2} sx={{ mt: 2 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label="Date"
              type="date"
              size="small"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 180 }}
            />
            <TextField
              label="Reason"
              size="small"
              fullWidth
              placeholder="e.g. National day of mourning"
              value={newReason}
              onChange={(e) => setNewReason(e.target.value)}
            />
          </Stack>

          <FormControlLabel
            control={
              <Switch checked={closedAllDay} onChange={(e) => setClosedAllDay(e.target.checked)} />
            }
            label="Closed all day"
          />

          {!closedAllDay && (
            <Stack direction="row" spacing={2}>
              <TextField
                label="Opens"
                type="time"
                size="small"
                value={newOpen}
                onChange={(e) => setNewOpen(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Closes"
                type="time"
                size="small"
                value={newClose}
                onChange={(e) => setNewClose(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}

          <Box>
            <Button variant="outlined" onClick={handleAddOverride} disabled={saving}>
              {saving ? 'Adding...' : 'Add override'}
            </Button>
          </Box>
        </Stack>

        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button onClick={onClose} variant="contained">
            Close
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}
